import { useInjection } from 'inversify-react';
import { IReactionDisposer, reaction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { ReactElement, useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useOnMount } from '../../../../common/hooks/useOnMount';
import { useAttachmentsPermissions } from '../../../../modules/attachments/Attachments.permissions';
import { AttachmentRenamedEvent, AttachmentRenamedEventType } from '../../../../modules/attachments/rename/events/AttachmentRenamedEvent';
import { IOverlayLoaderStore } from '../../../../modules/common/OverlayLoader.store';
import { Settings } from '../../../../modules/settings/SettingsContext';
import { ICurrentWorkspaceStore } from '../../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { useLoader } from '../../../helpers/useLoader';
import { Home } from '../../../routes/config/Home';
import { IEventBus } from '../../../services/eventBus.service';
import { IAuthStore } from '../../auth/auth.store';
import { IUserStore } from '../../user/user.store';
import { useAttachmentsService } from '../attachments.service';
import { AttachmentType, defaultAttachmentsPaging, IAttachment, IAttachmentsStore } from '../attachments.store';
import { IAttachmentUploadService } from '../attachmentUploadService';
import { AttachmentsList } from '../components/AttachmentsList';

interface IProps {
  addFolderEnabled: boolean;
}

export const AttachmentsListContainer = observer((props: IProps): ReactElement => {
  const attachmentsService = useAttachmentsService();
  const attachmentsUploadService = useInjection(IAttachmentUploadService.$);
  const authStore = useInjection(IAuthStore.$);
  const userStore = useInjection(IUserStore.$);
  const overlayLoaderStore = useInjection(IOverlayLoaderStore.$);
  const attachmentsPermissions = useAttachmentsPermissions();
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);
  const eventBus = useInjection(IEventBus.$);
  const attachmentStore = useInjection(IAttachmentsStore.$);

  const history = useHistory();

  const { attachmentId, workspaceId } = useParams<{ attachmentId: string; workspaceId: string }>();

  const { isLoading, withLoaderAsync } = useLoader();
  const context = useContext(Settings);

  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState<IAttachment | undefined>(undefined);
  const [lastSelectedAttachmentId, setLastSelectedAttachmentId] = useState<string | undefined>(undefined);

  useEffect(() => {
    const handleAttachmentRenamed = async (): Promise<void> => {
      await loadData();
    };

    const AttachmentRenamedDisposer = eventBus.addListener<AttachmentRenamedEvent>(handleAttachmentRenamed, AttachmentRenamedEventType);

    const reactionCallback = async (): Promise<void> => {
      const workspaceId = currentWorkspaceStore.currentWorkspace.id;
      if (workspaceId) {
        if (!attachmentId) {
          if (attachmentsPermissions.canViewAttachments()) {
            await loadData();
          }
          attachmentsService.store.clearSelectedAttachments();
        } else {
          history.push(Home.Attachments.List.Folder.withParams({ workspaceId, attachmentId: '' }));
        }
      }
    };

    const workspaceChangeReactionDisposer: IReactionDisposer = reaction(
      () => currentWorkspaceStore.currentWorkspace.id,
      () => {
        void reactionCallback();
      }
    );

    return (): void => {
      workspaceChangeReactionDisposer();
      AttachmentRenamedDisposer();
    };
  }, []);

  const loadData = async (): Promise<void> => {
    await withLoaderAsync(async () => attachmentsService.getAttachmentsAsync(attachmentId), 'attachment-list-loader');
  };

  const handleFolderSelect = (id: string): void => {
    history.push(Home.Attachments.List.Folder.withParams({ workspaceId: workspaceId, attachmentId: id }));
    attachmentsService.store.clearSelectedAttachments();
  };

  const handleDeleted = (id: string): void => {
    setIsDeleteConfirmationModalOpen(true);
    setAttachmentToDelete(attachmentsService.store.attachments.find((a) => a.id === id));
  };

  const handleDeleteConfirmed = async (): Promise<void> => {
    if (attachmentToDelete) {
      await attachmentsService.deleteAttachmentsAsync(false, [attachmentToDelete.id], attachmentId);
      setIsDeleteConfirmationModalOpen(false);
      setAttachmentToDelete(undefined);
    }
  };

  const handleDeleteCanceled = (): void => {
    setIsDeleteConfirmationModalOpen(false);
    setAttachmentToDelete(undefined);
  };

  const handleDownloadAttachments = async (ids: string[]): Promise<void> => {
    await attachmentsUploadService.downloadAttachmentsAsync(false, ids);
  };

  const handlePaginationChange = async (pageNumber: number, pageSize: number): Promise<void> => {
    await withLoaderAsync(async () => {
      await attachmentsService.getAttachmentsAsync(attachmentId, pageNumber, pageSize);
      context.setAttachmentsPageSize(pageSize);
    }, 'attachment-list-loader');
  };

  const handleOrderingChange = async (orderBy: string, orderType: string): Promise<void> => {
    const paging = attachmentsService.store.getPaging(workspaceId, attachmentId);
    paging.orderBy = orderBy;
    paging.orderType = orderType;
    attachmentsService.store.updatePaging(paging);
    await withLoaderAsync(async () => attachmentsService.getAttachmentsAsync(attachmentId), 'attachment-list-loader');
  };

  const handleSelectAttachment = (id: string): void => {
    setLastSelectedAttachmentId(id);

    const selectedAttachments = attachmentsService.store.selectedAttachments;

    if (selectedAttachments.ids.includes(id)) {
      selectedAttachments.ids.splice(selectedAttachments.ids.indexOf(id), 1);
    } else {
      selectedAttachments.ids.push(id);
    }

    attachmentsService.store.selectedAttachments.ids = selectedAttachments.ids.slice();
  };

  const handleSelectAllAttachments = (): void => {
    const selectedAttachments = attachmentsService.store.selectedAttachments;

    selectedAttachments.isAllSelected = !selectedAttachments.isAllSelected;
    selectedAttachments.ids = [];
  };

  const handleThumbCheck = (id: string, isBatch: boolean): void => {
    if (!isBatch) {
      handleSelectAttachment(id);
      return;
    }

    const attachments = attachmentsService.store.attachments;
    const lastIndex = attachments.findIndex((i) => i.id === lastSelectedAttachmentId);
    const markUpToIndex = attachments.findIndex((i) => i.id === id);

    if (lastIndex === undefined || markUpToIndex === undefined || lastIndex === -1 || markUpToIndex === -1) {
      handleSelectAttachment(id);
      return;
    }

    const changeDirection = lastIndex < markUpToIndex ? 1 : -1;
    const comparison = lastIndex < markUpToIndex ? (k: number, n: number): boolean => k <= n : (k: number, n: number): boolean => k >= n;

    const selectedAttachments = attachmentsService.store.selectedAttachments.ids;

    for (let i = lastIndex; comparison(i, markUpToIndex); i += changeDirection) {
      const attachment = attachments[i];
      const idToAdd = attachment.id;

      if (!selectedAttachments.includes(idToAdd)) {
        selectedAttachments.push(idToAdd);
      }
    }
  };

  const handleFileDownloadClick = async (id: string): Promise<void> => {
    const attachment = attachmentsService.store.attachments.find((a) => a.id === id);

    if (!attachment) {
      return;
    }

    switch (attachment.type) {
      case AttachmentType.FILE:
        await attachmentsUploadService.downloadAttachmentsAsync(false, [attachment.id], attachmentId);
        break;
      case AttachmentType.IMAGE_FILE:
      case AttachmentType.FOLDER:
        throw new Error('Cannot download image file or folder directly.');
    }
  };

  const isLoadingState = isLoading || attachmentsUploadService.data.progressData.length > 0 || overlayLoaderStore.isSpinnerVisible('attachment-list-loader');

  useOnMount(async () => {
    attachmentStore.attachmentsPaging = defaultAttachmentsPaging;

    await loadData();
  });

  const paging = attachmentsService.store.getPaging(workspaceId, attachmentId);
  paging.pageSize = context.store.attachmentsPageSize;

  return (
    <AttachmentsList
      addFolderEnabled={props.addFolderEnabled}
      breadcrumbs={attachmentsService.store.breadcrumbs.slice().reverse()}
      attachmentsListViewMode={userStore.attachmentsListViewMode}
      parentId={attachmentId}
      attachments={attachmentsService.store.attachments}
      currentDownloadAttachments={attachmentsUploadService.data.currentDownloadAttachment}
      isLoading={isLoadingState}
      isDeleteConfirmationModalOpen={isDeleteConfirmationModalOpen}
      attachmentToDelete={attachmentToDelete}
      workspaceId={workspaceId}
      pagination={paging}
      sorting={{
        orderBy: paging.orderBy ?? '',
        orderType: paging.orderType ?? '',
      }}
      selectedAttachments={{
        ids: attachmentsService.store.selectedAttachments.ids,
        isAllSelected: attachmentsService.store.selectedAttachments.isAllSelected,
      }}
      authToken={authStore.token}
      onFolderSelect={handleFolderSelect}
      onDownloadAttachments={handleDownloadAttachments}
      onDeleteCanceled={handleDeleteCanceled}
      onDeleteConfirmed={handleDeleteConfirmed}
      onDeleted={handleDeleted}
      onOrderingChange={handleOrderingChange}
      onPaginationChange={handlePaginationChange}
      onSelectAttachment={handleSelectAttachment}
      onSelectAllAttachments={handleSelectAllAttachments}
      onThumbCheck={handleThumbCheck}
      onFileDownload={handleFileDownloadClick}
      onThumbDelete={handleDeleted}
      getThumbnailUrl={(attachmentId) => attachmentsService.getAttachmentThumbnailUrl(attachmentId)}
    />
  );
});
